import { getOptions } from "@features/dropdownOptions/fetchOptions";
import type {
    StartWithWithoutGeneric,
    ClassicInputGroup,
    StartWithWithoutTierTwo,
    DropdownInputGroupTierTwo,
    ClassicInputGroupTierTwo,
    LucDropdownInputGroup,
    ModuleSchema,
    OptionalSubmodule,
    T2Schema,
    InputGroup,
    HectaresInputGroup,
} from "@typedefs/modulesInterfaces";
import { IrrigationPhase, IrrigationPhaseT2, Irrigations, IrrigationSystem, IrrigationSystemT2, type IrrigationPayload, type IrrigationT2Payload, type ModuleFull } from "./irrigationTypes";
import { FieldType } from "@typedefs/modulesInterfaces";
import { validatePercentage, validatePositiveNumber } from "../moduleUtils";
import { BEModulesClassNames } from "@utils/moduleList";
import * as Yup from "yup";
import { ModuleTypeRegistry } from "@utils/moduleTypeRegistry";

export const includedPropList = [
    "id",
    "note",
    "activity",
];
export const includedT2PropList = [
    "soc_t2_start",
    "soc_t2_w",
    "soc_t2_wo",
]


export const parentInitialValues: IrrigationPayload = {
    id: null,
    note: null,
    activity: null,
    start_year: null,
    soc_t2_start: null,
    soc_t2_w: null,
    soc_t2_wo: null,
    status: null,
    area: null
}
export const T2InitialValues: IrrigationT2Payload = {
    soc_t2_start: null,
    soc_t2_w: null,
    soc_t2_wo: null
}

const hectaresValidator = () => {
    return function (this: any, value: any) {
        if (value) {
            const context = this as any;
            const moduleFull = context?.from?.[1].value as ModuleFull;
            const parentArea = moduleFull.module.area;

            return !parentArea || value <= parentArea;
        }
        return true;
    };
};

export const validationSchema = Yup.object({
    irrigations: Yup.array().of(
        Yup.object().shape({
            irrigation_system_type: validatePositiveNumber.required('validations.irrigation_system_required'),
            ha_start: validatePositiveNumber.required('validations.hectares_required')
                .test('ha-not-greater-than-area', 'validations.hectares_not_greater_than_area', hectaresValidator()),
            ha_w: validatePositiveNumber.required('validations.hectares_required')
                .test('ha-not-greater-than-area', 'validations.hectares_not_greater_than_area', hectaresValidator()),
            ha_wo: validatePositiveNumber.required('validations.hectares_required')
                .test('ha-not-greater-than-area', 'validations.hectares_not_greater_than_area', hectaresValidator()),
        })
    ),
    irrigationPhases: Yup.array().of(
        Yup.object().shape({
            irrigation_system_type: validatePositiveNumber.required('validations.irrigation_system_required'),
            well_depth: validatePositiveNumber.required('validations.depth_of_well_required'),
            fuel_type_start: validatePositiveNumber.required('validations.fuel_type_required'),
            fuel_type_w: validatePositiveNumber.required('validations.fuel_type_required'),
            fuel_type_wo: validatePositiveNumber.required('validations.fuel_type_required'),
            ha_start: validatePositiveNumber.required('validations.hectares_required')
                .test('ha-not-greater-than-area', 'validations.hectares_not_greater_than_area', hectaresValidator()),
            ha_w: validatePositiveNumber.required('validations.hectares_required')
                .test('ha-not-greater-than-area', 'validations.hectares_not_greater_than_area', hectaresValidator()),
            ha_wo: validatePositiveNumber.required('validations.hectares_required')
                .test('ha-not-greater-than-area', 'validations.hectares_not_greater_than_area', hectaresValidator()),
            gross_irrigation_water_start: validatePositiveNumber.required('validations.gross_irrigation_water_required'),
            gross_irrigation_water_w: validatePositiveNumber.required('validations.gross_irrigation_water_required'),
            gross_irrigation_water_wo: validatePositiveNumber.required('validations.gross_irrigation_water_required'),
            transmission_loss_t2_start: validatePercentage,
            transmission_loss_t2_w: validatePercentage,
            transmission_loss_t2_wo: validatePercentage,
            pumping_efficiency_t2_start: validatePercentage,
            pumping_efficiency_t2_w: validatePercentage,
            pumping_efficiency_t2_wo: validatePercentage,
        })
    ),
});

export const initialValues: ModuleFull = {
    irrigations: [],
    irrigationPhases: [],
    module: parentInitialValues,
    tiertwo: T2InitialValues
};

const moduleRegistry = ModuleTypeRegistry.getInstance();

// Lazy evaluation functions that get IDs when called
const getOperationPhaseModuleType = () => moduleRegistry.getIdByClassName(BEModulesClassNames.OperationPhaseOfIrrigation);
const getIrrigationSystemModuleType = () => moduleRegistry.getIdByClassName(BEModulesClassNames.IrrigationSystem);

const irrigationSystemTypes = () => {
    const moduleType = getIrrigationSystemModuleType();
    return getOptions("irrigationSystemTypes", { module_types: moduleType });
};

const irrigationSystemTypesOperationPhase = () => {
    const moduleType = getOperationPhaseModuleType();
    return getOptions("irrigationSystemTypes", { module_types: moduleType });
};

const fuelTypes = () => {
    const moduleType = getOperationPhaseModuleType();
    return getOptions("fuelTypes", { module_types: moduleType });
};
const countries = () => getOptions("country");
const efSources = () => getOptions("emissionFactorSources");

const getInitInputGroups: () => InputGroup[] = () => {
    const hectaresInputGroup: HectaresInputGroup = {
        type: FieldType.HECTARES,
        label: "module.hectares",
        unit: "[ha]",
        inputName: "module.area",
        disabled: true,
    };

    return [hectaresInputGroup]
}

const getIrrigationSystemSchema: () => OptionalSubmodule<IrrigationSystem> = () => {
    const systemType: LucDropdownInputGroup = {
        type: FieldType.SELECT_LUC,
        label: "module.type_of_irrigation_required",
        inputName: (index: number) => `irrigations.${index}.irrigation_system_type`,
        dropdownOptions: irrigationSystemTypes,
    };
    const systemHectares: StartWithWithoutGeneric = {
        type: FieldType.SWW,
        label: "module.hectares_required",
        unit: "[ha]",
        inputName: (index: number) => `irrigations.${index}.ha`,
    };

    return {
        name: Irrigations.IrrigationSystem,
        inputGroups: [systemType, systemHectares],
        inputName: "irrigations",
        repeatable: true,
        route: "irrigation-systems",
        cacheTag: "IrrigationSystemsDefaults"
    };
};

const getOperationPhaseSchema: () => OptionalSubmodule<IrrigationPhase> = () => {
    const operationType: LucDropdownInputGroup = {
        type: FieldType.SELECT_LUC,
        label: "module.type_of_irrigation_required",
        inputName: (index: number) => `irrigationPhases.${index}.irrigation_system_type`,
        dropdownOptions: irrigationSystemTypesOperationPhase,
    };
    const depthOfWell: ClassicInputGroup = {
        type: FieldType.CLASSIC,
        label: "module.depth_of_well_required",
        inputName: (index: number) => `irrigationPhases.${index}.well_depth`,
        unit: "[m]",
        inputType: "number",
    };
    const sourceOfEnergy: StartWithWithoutGeneric = {
        type: FieldType.SWW_SELECT,
        label: "module.source_of_energy_required",
        inputName: (index: number) => `irrigationPhases.${index}.fuel_type`,
        startProps: { dropdownOptions: fuelTypes },
        withProps: { dropdownOptions: fuelTypes },
        withoutProps: { dropdownOptions: fuelTypes },
    }
    const operationHectares: StartWithWithoutGeneric = {
        type: FieldType.SWW,
        label: "module.hectares_required",
        unit: "[ha]",
        inputName: (index: number) => `irrigationPhases.${index}.ha`,
    };
    const grossIrrigationWater: StartWithWithoutGeneric = {
        type: FieldType.SWW,
        label: "module.gross_irrigation_water_required",
        unit: "[mm/yr]",
        inputName: (index: number) => `irrigationPhases.${index}.gross_irrigation_water`,
    };

    return {
        name: Irrigations.OperationPhase,
        inputGroups: [operationType, depthOfWell, sourceOfEnergy, operationHectares, grossIrrigationWater],
        inputName: "irrigationPhases",
        repeatable: true,
        route: "irrigation-phases",
        cacheTag: "IrrigationPhasesDefaults"
    };
};

const getOptionalSubmodules: () => OptionalSubmodule<IrrigationSystem | IrrigationPhase>[] = () => {
    const irrigationSystem = getIrrigationSystemSchema();
    const irrigationPhase = getOperationPhaseSchema()

    return [irrigationSystem, irrigationPhase]
}

const getT2IrrigationSchema: () => OptionalSubmodule<IrrigationSystemT2> = () => {
    const efNewIrrigation: StartWithWithoutTierTwo = {
        type: FieldType.SWW_T2,
        label: "module.ef_new_irrigation",
        inputName: (index: number) => `irrigations.${index}.ef_t2`,
        unit: "[kg CO2-e/ha]",
        links: ["Type of Irrigation"],
        startProps: { inputType: "number", defaultValue: 0 },
        withoutProps: { inputType: "number", defaultValue: 0 },
        withProps: { inputType: "number", defaultValue: 0 },
    };

    return {
        name: Irrigations.IrrigationSystem,
        inputName: "irrigations",
        inputGroups: [efNewIrrigation],
        route: 'irrigation-systems',
        cacheTag: 'IrrigationSystemsDefaults'
    };
};

const labelSuffixStart = (index: number) => `irrigationPhases.${index}.fuel_type_start_unit`
const labelSuffixWithout = (index: number) => `irrigationPhases.${index}.fuel_type_wo_unit`
const labelSuffixWith = (index: number) => `irrigationPhases.${index}.fuel_type_w_unit`

const getT2OperationPhaseSubmodules: () => OptionalSubmodule<IrrigationPhaseT2> = () => {
    const countryOfOrigin: DropdownInputGroupTierTwo = {
        type: FieldType.SELECT_T2,
        label: "module.power_country_of_origin",
        inputName: (index: number) => `irrigationPhases.${index}.country_t2`,
        dropdownOptions: countries,
        defaultValue: "value",
        unit: "",
        links: [],
    };
    const emissionFactorSources: DropdownInputGroupTierTwo = {
        type: FieldType.SELECT_T2,
        label: "module.ef_source_of_electricity",
        inputName: (index: number) => `irrigationPhases.${index}.ef_source`,
        dropdownOptions: efSources,
        unit: "",
        links: [],
        defaultValue: "",
        hideDefaultLabel: true,
    }
    const efCo2Irrigation: StartWithWithoutTierTwo = {
        type: FieldType.SWW_T2,
        label: "module.ef_of_fuel_used_co2",
        inputName: (index: number) => `irrigationPhases.${index}.ef_co2_t2`,
        unit: "[t CO2-e/m3]",
        links: ["Type of Irrigation"],
        startProps: { inputType: "number", defaultValue: 0, labelSuffix: labelSuffixStart },
        withoutProps: { inputType: "number", defaultValue: 0, labelSuffix: labelSuffixWithout },
        withProps: { inputType: "number", defaultValue: 0, labelSuffix: labelSuffixWith },
    };
    const efCh4Irrigation: StartWithWithoutTierTwo = {
        type: FieldType.SWW_T2,
        label: "module.ef_of_fuel_used_ch4",
        inputName: (index: number) => `irrigationPhases.${index}.ef_ch4_t2`,
        unit: "[t CH4-e/m3]",
        links: ["Type of Irrigation"],
        startProps: { inputType: "number", defaultValue: 0, labelSuffix: labelSuffixStart },
        withoutProps: { inputType: "number", defaultValue: 0, labelSuffix: labelSuffixWithout },
        withProps: { inputType: "number", defaultValue: 0, labelSuffix: labelSuffixWith },
    };
    const efN2oIrrigation: StartWithWithoutTierTwo = {
        type: FieldType.SWW_T2,
        label: "module.ef_of_fuel_used_n2o",
        inputName: (index: number) => `irrigationPhases.${index}.ef_n2o_t2`,
        unit: "[t N2O-e/m3]",
        links: ["Source of Energy"],
        startProps: { inputType: "number", defaultValue: 0, labelSuffix: labelSuffixStart },
        withoutProps: { inputType: "number", defaultValue: 0, labelSuffix: labelSuffixWithout },
        withProps: { inputType: "number", defaultValue: 0, labelSuffix: labelSuffixWith },
    };
    const transmissionLoss: StartWithWithoutTierTwo = {
        type: FieldType.SWW_T2,
        inputName: (index: number) => `irrigationPhases.${index}.transmission_loss_t2`,
        label: "module.transmission_loss",
        unit: "[%]",
        links: [],
        startProps: { inputType: "number", defaultValue: 0 },
        withoutProps: { inputType: "number", defaultValue: 0 },
        withProps: { inputType: "number", defaultValue: 0 },
    };
    const averagePressure: ClassicInputGroupTierTwo = {
        type: FieldType.CLASSIC_T2,
        inputName: (index: number) => `irrigationPhases.${index}.average_pressure_t2`,
        inputType: "number",
        label: "module.average_pressure",
        unit: "[bar]",
        links: ["Type of Irrigation"],
        defaultValue: "value",
    };
    const totalDynamicHead: ClassicInputGroupTierTwo = {
        type: FieldType.CLASSIC_T2,
        inputName: (index: number) => `irrigationPhases.${index}.total_dynamic_head_t2`,
        inputType: "number",
        label: "module.total_dynamic_head",
        unit: "[m/head]",
        links: ["Type of Irrigation"],
        defaultValue: "value",
    };
    const pumpingEfficiency: StartWithWithoutTierTwo = {
        type: FieldType.SWW_T2,
        inputName: (index: number) => `irrigationPhases.${index}.pumping_efficiency_t2`,
        label: "module.pumping_efficiency",
        unit: "[%]",
        links: [],
        startProps: { inputType: "number", defaultValue: 0 },
        withoutProps: { inputType: "number", defaultValue: 0 },
        withProps: { inputType: "number", defaultValue: 0 },
    };

    return {
        name: Irrigations.OperationPhase,
        inputName: 'irrigationPhases',
        inputGroups: [
            countryOfOrigin,
            emissionFactorSources,
            efCo2Irrigation,
            efCh4Irrigation,
            efN2oIrrigation,
            transmissionLoss,
            averagePressure,
            totalDynamicHead,
            pumpingEfficiency,
        ],
        route: "irrigation-phases",
        cacheTag: "IrrigationPhasesDefaults",
    };
};

const getT2OptionalSubmodules: () => OptionalSubmodule<IrrigationSystemT2 | IrrigationPhaseT2>[] = () => {
    const irrigationsT2 = getT2IrrigationSchema()
    const irrigationPhasesT2 = getT2OperationPhaseSubmodules()

    return [irrigationsT2, irrigationPhasesT2];
}


export const getSchemas = () => {
    const moduleSchema: ModuleSchema = {
        initInputGroups: getInitInputGroups(),
        mandatorySubmodules: [],
        optionalSubmodules: getOptionalSubmodules(),
    }

    const t2Schema: T2Schema = {
        submodules: [],
        optionalSubmodules: getT2OptionalSubmodules()
    }

    return { moduleSchema, t2Schema }
}


